import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useRole } from "@/lib/useRole";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, ShieldAlert, MinusCircle, Lock, History, Loader2 } from "lucide-react";
import { RPT_STATUS_BG, SEC_STATUS_BG } from "@/components/ReportPreview";

const STATE_META = {
  Complete: { icon: CheckCircle2, cls: "text-teal-600" },
  Warning: { icon: AlertTriangle, cls: "text-amber-600" },
  "Blocking issue": { icon: ShieldAlert, cls: "text-red-600" },
  "Not applicable": { icon: MinusCircle, cls: "text-slate-300" },
};

export default function ReportApprovals({ report, onUpdated, onNavigate }) {
  const [readiness, setReadiness] = useState(null);
  const [release, setRelease] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const { canApprove, canLock } = useRole();

  const loadChecks = () => {
    if (!report) return;
    setLoadError(false);
    Promise.all([
      api.get(`/reports/registry/${report.id}/readiness`).then((r) => setReadiness(r.data)),
      api.get(`/reports/registry/${report.id}/release`).then((r) => setRelease(r.data)),
    ]).catch(() => setLoadError(true));
  };
  useEffect(loadChecks, [report?.id, report?.last_updated]);

  const followLink = (link) => {
    if (link.view === "reports" && link.target) {
      const el = document.getElementById(link.target);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("bg-amber-50"); setTimeout(() => el.classList.remove("bg-amber-50"), 2500); return; }
    }
    if (link.view && link.view !== "reports" && onNavigate) onNavigate(link.view, link.target);
  };

  if (!report) return <p className="text-sm text-slate-400 p-6">Select a report from the Report Library.</p>;

  const changeStatus = (status) => {
    setBusy(true);
    api.patch(`/reports/registry/${report.id}/status`, { status })
      .then((r) => { onUpdated(r.data); toast.success(`Report status → ${status}`); })
      .catch((e) => {
        const d = e.response?.data?.detail;
        if (d?.blocking || d?.mandatory_sections_unapproved) {
          toast.error(`Blocked: ${[...(d.blocking || []), ...(d.mandatory_sections_unapproved || [])].slice(0, 3).join("; ")}…`);
        } else toast.error(typeof d === "string" ? d : "Status change failed");
      })
      .finally(() => setBusy(false));
  };

  const locked = report.status === "Locked final";

  return (
    <div className="space-y-4" data-testid="approvals-workspace" aria-busy={busy}>
      {loadError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 flex items-center gap-3" role="alert" data-testid="approvals-load-error">
          Readiness and release checks could not be loaded.
          <Button variant="outline" className="h-7 text-[11px]" onClick={loadChecks} data-testid="approvals-retry-btn">Retry</Button>
        </div>
      )}
      <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-sm font-bold text-slate-900">{report.title}</h4>
          <Badge variant="outline" className={`text-[9px] ${RPT_STATUS_BG[report.status]}`}>{report.status}</Badge>
          <span className="text-[10px] text-slate-400">v{report.version} · owner {report.owner} · reviewer {report.reviewer} · approver {report.approver}</span>
        </div>
        {report.locked && <p className="text-[10.5px] text-emerald-700 mt-1.5 flex items-center gap-1"><Lock className="w-3 h-3" />Locked by {report.locked.by} on {report.locked.date} — {report.locked.note}</p>}
        {!locked && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {["Content validation in progress", "Under scientific review", "Revision required"].map((st) => (
              <Button key={st} variant="outline" className="h-7 text-[10.5px]" disabled={busy || report.status === st} onClick={() => changeStatus(st)} data-testid={`status-${st.toLowerCase().replace(/ /g, "-")}`}>{st}</Button>
            ))}
            {canApprove && (
              <Button variant="outline" className="h-7 text-[10.5px]" disabled={busy || report.status === "Approved for reporting"} onClick={() => changeStatus("Approved for reporting")} data-testid="status-approved-for-reporting">Approved for reporting</Button>
            )}
            {canLock && (
              <Button className="h-7 text-[10.5px] bg-slate-900 hover:bg-slate-800 text-white" disabled={busy || !readiness?.can_lock} onClick={() => changeStatus("Locked final")} data-testid="lock-final-btn">
                <Lock className="w-3 h-3 mr-1" />Lock final {!readiness?.can_lock && "(blocked)"}
              </Button>
            )}
            {!canApprove && (
              <span className="text-[10px] text-slate-500 self-center" data-testid="status-role-note">Approval and lock require Scientific/Senior Reviewer or Administrator — server-enforced.</span>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="readiness-panel">
        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Report-readiness check · {readiness ? `${readiness.checks.filter((c) => c.state === "Complete").length}/${readiness.checks.length} complete` : "…"}</p>
        {!readiness && !loadError && (
          <p className="text-[11px] text-slate-400 flex items-center gap-1.5" role="status" aria-live="polite" data-testid="readiness-loading">
            <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />Running readiness checks…
          </p>
        )}
        {readiness && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-2">
            {readiness.checks.map((c) => {
              const M = STATE_META[c.state] || STATE_META["Not applicable"];
              const Icon = M.icon;
              return (
                <div key={c.name} className="flex items-start gap-2" data-testid={`check-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <Icon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${M.cls}`} />
                  <div className="min-w-0">
                    <p className="text-[11.5px] font-semibold text-slate-700">{c.name} <span className={`font-normal ${M.cls}`}>— {c.state}</span></p>
                    <p className="text-[10.5px] text-slate-500 leading-snug">{c.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {readiness && <p className="text-[10px] text-slate-400 mt-3 border-t border-slate-100 pt-2">{readiness.note}</p>}
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="release-control-panel">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400">Release control · Approved / External Final export</p>
          {release && (
            <Badge variant="outline" className={`text-[9px] ${release.can_export_approved ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-red-50 text-red-700 border-red-200"}`} data-testid="release-status-badge">
              {release.can_export_approved ? "Clear for approved export" : `Blocked — ${release.blocker_count} blocker(s)`}
            </Badge>
          )}
        </div>
        {!release && !loadError && (
          <p className="text-[11px] text-slate-400 flex items-center gap-1.5" role="status" aria-live="polite" data-testid="release-loading">
            <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />Evaluating release blockers…
          </p>
        )}
        {release && (
          <>
            <p className="text-[10.5px] text-slate-500 leading-snug mb-2.5">{release.policy}</p>
            <p className="text-[10px] text-teal-700 font-semibold mb-2.5">Watermarked draft export remains available at all times.</p>
            <div className="space-y-1.5" data-testid="release-blockers">
              {release.blockers.map((b) => (
                <div key={b.id} className="flex flex-wrap items-start justify-between gap-2 border border-red-100 bg-red-50/40 rounded-lg px-3 py-2" data-testid={`release-blocker-${b.id}`}>
                  <div className="flex-1 min-w-[240px]">
                    <p className="text-[10.5px]"><Badge variant="outline" className="text-[8.5px] bg-red-50 text-red-700 border-red-200 mr-1.5">{b.category}</Badge><span className="text-slate-700">{b.detail}</span></p>
                  </div>
                  <button onClick={() => followLink(b.link)} data-testid={`release-blocker-link-${b.id}`}
                    aria-label={`Resolve blocker: ${b.link.label}`}
                    className="text-[10px] font-semibold text-teal-700 hover:text-teal-900 whitespace-nowrap">{b.link.label} →</button>
                </div>
              ))}
              {release.blockers.length === 0 && <p className="text-[11px] text-teal-700">No release blockers — an approved, watermark-free export is permitted.</p>}
            </div>
          </>
        )}
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm overflow-x-auto" data-testid="section-matrix">
        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Section approval matrix — every mandatory section must reach "Approved for reporting" before Locked Final</p>
        <table className="w-full text-xs" aria-label="Section approval matrix">
          <thead><tr className="text-left border-b border-slate-200">
            {["Section", "Mandatory", "Status", "Reviewer", "Last reviewed", "Citations", "Open issues"].map((h) => (
              <th key={h} scope="col" className="py-1.5 pr-3 font-mono text-[9px] uppercase tracking-widest text-slate-400">{h}</th>))}
          </tr></thead>
          <tbody>
            {report.sections.map((sec) => (
              <tr key={sec.id} id={`section-row-${sec.id}`} className="border-b border-slate-100 transition-colors duration-500">
                <td className="py-2 pr-3 font-semibold text-slate-700">{sec.title}</td>
                <td className="py-2 pr-3 text-slate-500">{sec.mandatory ? "Yes" : "—"}</td>
                <td className="py-2 pr-3"><Badge variant="outline" className={`text-[9px] ${SEC_STATUS_BG[sec.status]}`}>{sec.status}</Badge></td>
                <td className="py-2 pr-3 text-slate-500">{sec.reviewer || "Unassigned"}</td>
                <td className="py-2 pr-3 text-slate-500">{sec.last_reviewed || "—"}</td>
                <td className="py-2 pr-3 text-slate-500">{sec.citation_check}</td>
                <td className={`py-2 pr-3 ${sec.open_issues ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{sec.open_issues}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="version-history">
        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1"><History className="w-3 h-3" />Version history — every save or material regeneration creates a version record; locked versions are never silently edited</p>
        <div className="space-y-2">
          {(report.versions || []).slice().reverse().map((v) => (
            <div key={v.version} className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-[11px] text-slate-600">
              <div className="flex flex-wrap items-center gap-1"><span className="font-semibold text-slate-800">v{v.version}</span> · {v.date} · {v.created_by} · <Badge variant="outline" className={`text-[8.5px] ${RPT_STATUS_BG[v.approval] || "bg-slate-50 text-slate-500 border-slate-200"}`}>{v.approval}</Badge></div>
              <p className="mt-0.5">{v.changes}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Sections changed: {Array.isArray(v.sections_changed) ? v.sections_changed.join(", ") : v.sections_changed} · {v.sources_changed}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="report-audit">
        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Audit trail</p>
        {(report.audit || []).slice().reverse().map((a, i) => (
          <p key={i} className="text-[11px] text-slate-600 py-0.5"><span className="font-mono text-[10px] text-slate-400 mr-2">{a.date}</span><span className="font-semibold">{a.user}</span> — {a.action}</p>
        ))}
      </div>
    </div>
  );
}
